import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Icon } from '@/shared/ui/Icon';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { ErrorState } from '@/shared/ui/ErrorState';

import { useToast } from '@/shared/ui/Toast';
import { type ChatMessage, type ChatPage as ChatPageT, listMessages, sendMessage } from '@/shared/api/chat';
import { getMeeting } from '@/shared/api/meetings';
import { createPlaceCandidate } from '@/shared/api/voting';
import { guessCategory, resolvePlaceLink } from '@/features/place/api';
import { useAuthStore } from '@/shared/auth/store';
import { fmtKst } from '@/shared/lib/date';
import { MeetingTabs } from '@/features/meeting/MeetingTabs';
import { useChatSocket } from '@/features/chat/useChatSocket';

// 실시간 채팅: STOMP WebSocket 수신 + REST 전송. 소켓이 막힌 환경 대비 느린 폴링(20초) 폴백 유지.
const FALLBACK_POLL_MS = 20000;

// 백엔드 LinkResolver 허용 호스트와 동일 (place/domain/PlaceService)
const MAP_LINK =
  /https?:\/\/(?:naver\.me|kko\.kakao\.com|place\.map\.kakao\.com|map\.naver\.com)\/[^\s]+/;

function extractMapLink(content: string): string | null {
  return MAP_LINK.exec(content)?.[0] ?? null;
}

// 카톡 그룹핑 키 — 같은 사람이 같은 분(分)에 보낸 메시지는 한 묶음
function minuteKey(iso: string): string {
  return fmtKst(iso, 'yyyy-MM-dd HH:mm');
}

function dayKey(iso: string): string {
  return fmtKst(iso, 'yyyy-MM-dd');
}

function sameGroup(a: ChatMessage | undefined, b: ChatMessage): boolean {
  return !!a && a.userId === b.userId && minuteKey(a.createdAt) === minuteKey(b.createdAt);
}

export default function ChatPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const toast = useToast();
  const [draft, setDraft] = useState('');
  const [liveMessage, setLiveMessage] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const lastIdRef = useRef<string | null>(null);

  const meetingQ = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => getMeeting(id),
    enabled: !!id,
  });

  const chatQ = useInfiniteQuery({
    queryKey: ['chat', id],
    queryFn: ({ pageParam }) => listMessages(id, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: FALLBACK_POLL_MS,
    enabled: !!id,
  });

  // 소켓 수신 메시지를 최신 페이지(pages[0], 최신→과거) 앞에 삽입. id 중복은 무시.
  const appendMessage = useCallback(
    (msg: ChatMessage) => {
      qc.setQueryData<InfiniteData<ChatPageT, string | null>>(['chat', id], (prev) => {
        if (!prev) return prev;
        const exists = prev.pages.some((pg) => pg.items.some((m) => m.id === msg.id));
        if (exists) return prev;
        const [first, ...rest] = prev.pages;
        const newFirst: ChatPageT = { ...first, items: [msg, ...first.items] };
        return { ...prev, pages: [newFirst, ...rest] };
      });
    },
    [qc, id],
  );

  // 재연결 시 끊긴 동안 누락분을 한 번에 보정
  const resync = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['chat', id] });
  }, [qc, id]);

  useChatSocket({ meetingId: id, enabled: !!id, onMessage: appendMessage, onResync: resync });

  // pages[0] = 최신 페이지(최신→과거) → 화면 표시는 과거→최신
  const messages = useMemo<ChatMessage[]>(() => {
    const all = chatQ.data?.pages.flatMap((p) => p.items) ?? [];
    return [...all].reverse();
  }, [chatQ.data]);

  const newestId = messages.length > 0 ? messages[messages.length - 1].id : null;
  useEffect(() => {
    if (newestId && newestId !== lastIdRef.current) {
      const hadPrevious = Boolean(lastIdRef.current);
      lastIdRef.current = newestId;
      bottomRef.current?.scrollIntoView({ block: 'end' });
      if (hadPrevious) {
        const latest = messages[messages.length - 1];
        setLiveMessage(
          latest.userId === user?.id
            ? '메시지를 보냈어요.'
            : `${latest.nickname}님의 새 메시지: ${latest.content.slice(0, 40)}`,
        );
      }
    }
  }, [messages, newestId, user?.id]);

  const sendMut = useMutation({
    mutationFn: (content: string) => sendMessage(id, content),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['chat', id] });
    },
    onError: () => toast.error('메시지 전송에 실패했어요.'),
  });

  const registerPlaceMut = useMutation({
    mutationFn: async (url: string) => {
      const place = await resolvePlaceLink(url);
      return createPlaceCandidate(id, {
        name: place.name,
        category: guessCategory(place.category, place.name),
        url: place.placeUrl || url,
        address: place.roadAddress || place.address,
        x: String(place.x),
        y: String(place.y),
      });
    },
    onSuccess: (candidate) => {
      qc.invalidateQueries({ queryKey: ['places', id] });
      toast.success(`'${candidate.name}'을(를) 장소 후보로 등록했어요.`);
    },
    onError: () => toast.error('링크에서 장소를 찾지 못했어요.'),
  });

  function onSend(e: FormEvent) {
    e.preventDefault();
    const content = draft.trim();
    if (!content || sendMut.isPending) return;
    sendMut.mutate(content);
  }

  if (chatQ.isLoading) return <SkeletonCard />;
  if (chatQ.isError) {
    return <ErrorState title="채팅을 불러오지 못했어요" onRetry={() => chatQ.refetch()} />;
  }

  const status = meetingQ.data?.status;
  const meetingPoint = meetingQ.data?.meetingPointName ?? '집결지 미정';

  return (
    <section className="flex min-h-[calc(100vh-130px)] flex-col bg-canvas pb-[86px]">
      <h1 className="sr-only">채팅</h1>
      <MeetingTabs meetingId={id} />

      <div className="mx-4 mt-3 rounded-[20px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm px-3 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold text-ink-faint">현재 모임 요약</p>
            <p className="mt-1 truncate text-[14px] font-semibold text-ink">{meetingPoint}</p>
          </div>
          <div className="flex shrink-0 gap-1.5">
            <Link
              to={`/meetings/${id}/live`}
              className="rounded-full bg-primary-soft px-2.5 py-1.5 text-[11px] font-semibold text-primary focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              LIVE
            </Link>
            <Link
              to={`/meetings/${id}/places?from=chat`}
              className="rounded-full bg-surface-muted px-2.5 py-1.5 text-[11px] font-semibold text-ink-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              코스
            </Link>
          </div>
        </div>
        {status === 'VOTING' ? (
          <Link
            to={`/meetings/${id}/vote`}
            className="mt-2 flex items-center gap-2 rounded-xl bg-primary-soft/60 px-3 py-2 text-small font-semibold text-primary"
          >
            <Icon name="vote" size={15} />
            날짜·장소 후보 확인하기
          </Link>
        ) : status === 'SETTLING' ? (
          <Link
            to={`/meetings/${id}/settlement`}
            className="mt-2 flex items-center gap-2 rounded-xl bg-primary-soft/60 px-3 py-2 text-small font-semibold text-primary"
          >
            <Icon name="wallet" size={15} />
            내 정산 확인하기
          </Link>
        ) : null}
      </div>

      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {liveMessage}
      </div>
      <div className="flex flex-1 flex-col px-4 pt-3">
        {chatQ.hasNextPage ? (
          <button
            type="button"
            className="mb-3 w-full rounded-xl border border-surface-border bg-surface px-3 py-2 text-small font-medium text-ink-muted focus-visible:outline-none focus-visible:shadow-focus-ring disabled:opacity-60"
            disabled={chatQ.isFetchingNextPage}
            onClick={() => chatQ.fetchNextPage()}
          >
            {chatQ.isFetchingNextPage ? '불러오는 중…' : '이전 메시지 보기'}
          </button>
        ) : null}

        {messages.length === 0 ? (
          <div className="flex min-h-[220px] flex-col items-center justify-center px-6 text-center">
            <p className="text-[15px] font-semibold text-ink">첫 메시지를 보내보세요</p>
            <p className="mt-1.5 text-[12px] leading-5 text-ink-muted">
              지도 링크를 보내면 장소 후보로 바로 등록할 수 있어요.
            </p>
          </div>
        ) : (
          <ol className="flex-1 space-y-0.5" aria-label="채팅 메시지" role="log" aria-live="polite" aria-relevant="additions">
            {messages.map((m, i) => {
              const mine = m.userId === user?.id;
              const mapLink = extractMapLink(m.content);
              const prev = messages[i - 1];
              const next = messages[i + 1];
              const newDay = !prev || dayKey(prev.createdAt) !== dayKey(m.createdAt);
              const startsGroup = newDay || !sameGroup(prev, m);
              const endsGroup = !next || !sameGroup(m, next);
              const initial = (m.nickname || '익명').trim().slice(0, 1) || '익';
              const avatarKey = String(m.userId ?? m.nickname ?? m.id ?? '익명');
              const avatarTone = ['bg-primary', 'bg-success', 'bg-warning', 'bg-primary-strong'][
                Math.abs(avatarKey.split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0)) % 4
              ];

              return (
                <li key={m.id}>
                  {newDay ? (
                    <div className="my-3 flex items-center gap-2" role="separator">
                      <span className="h-px flex-1 bg-surface-border" />
                      <span className="text-tag font-medium text-ink-faint">
                        {fmtKst(m.createdAt, 'yyyy년 M월 d일 (E)')}
                      </span>
                      <span className="h-px flex-1 bg-surface-border" />
                    </div>
                  ) : null}
                  <div
                    className={`flex items-end gap-2 ${mine ? 'flex-row-reverse' : ''} ${
                      startsGroup && !newDay ? 'mt-2' : 'mt-0.5'
                    }`}
                  >
                    {!mine ? (
                      <span
                        aria-hidden="true"
                        className={`mb-0.5 flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white ${
                          startsGroup ? avatarTone : 'invisible'
                        }`}
                      >
                        {initial}
                      </span>
                    ) : null}
                    <div className={`flex max-w-[78%] flex-col gap-0.5 ${mine ? 'items-end' : ''}`}>
                      {!mine && startsGroup ? (
                        <p className="pl-0.5 text-tag font-semibold text-ink-faint">
                          {m.nickname}
                        </p>
                      ) : null}
                      <div className={`flex items-end gap-1 ${mine ? 'flex-row-reverse' : ''}`}>
                        <div
                          className={`whitespace-pre-wrap break-words px-3 py-2 text-left text-small leading-[1.5] ${
                            mine
                              ? 'rounded-[16px_5px_16px_16px] bg-[image:var(--hero-grad)] text-white'
                              : startsGroup
                                ? 'rounded-[5px_16px_16px_16px] border border-surface-border bg-surface text-ink'
                                : 'rounded-[16px] border border-surface-border bg-surface text-ink'
                          }`}
                        >
                          {m.content}
                        </div>
                        {endsGroup ? (
                          <span className="mb-1 shrink-0 text-[9px] text-ink-faint">
                            {fmtKst(m.createdAt, 'HH:mm')}
                          </span>
                        ) : null}
                      </div>
                      {mapLink ? (
                        <button
                          type="button"
                          className="mt-1 flex max-w-full items-center gap-2 rounded-xl border border-surface-border bg-surface px-3 py-2 text-left disabled:opacity-60"
                          disabled={registerPlaceMut.isPending}
                          onClick={() => registerPlaceMut.mutate(mapLink)}
                        >
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[9px] bg-success-soft text-success">
                            <Icon name="pin" size={15} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-small font-semibold text-ink">
                              장소 후보로 등록
                            </span>
                            <span className="block truncate text-tag text-ink-faint">
                              {registerPlaceMut.isPending ? '등록 중…' : mapLink}
                            </span>
                          </span>
                          <Icon name="right" size={13} className="text-ink-faint" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        <div ref={bottomRef} />
      </div>

      <form
        onSubmit={onSend}
        className="fixed inset-x-0 bottom-[62px] z-30 mx-auto w-full max-w-screen-sm border-t border-surface-border bg-surface px-3 py-2.5"
      >
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            rows={1}
            onChange={(e) => {
              setDraft(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSend(e as unknown as FormEvent);
              }
            }}
            placeholder="메시지 입력..."
            maxLength={500}
            aria-label="메시지 입력"
            className="min-w-0 flex-1 resize-none overflow-y-auto rounded-[20px] bg-surface-muted px-3.5 py-2 text-small text-ink placeholder:text-ink-faint focus-visible:outline-none focus-visible:shadow-focus-ring"
            style={{ height: '38px' }}
          />
          <button
            type="submit"
            disabled={sendMut.isPending || draft.trim().length === 0}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white focus-visible:outline-none focus-visible:shadow-focus-ring disabled:bg-surface-border disabled:text-ink-faint"
            aria-label="메시지 전송"
          >
            <Icon name="right" size={15} />
          </button>
        </div>
      </form>
    </section>
  );
}
